/**
 * Le seul endroit qui parle au portail.
 *
 * **Une enveloppe, un curseur, une version** — décision `0014`. Toute liste
 * rend `{donnees, suite}` ; `suite` est opaque et se renvoie tel quel. Toute
 * écriture peut porter `If-Match` avec la version lue, et reçoit **409** si
 * l'objet a changé entre-temps.
 *
 * **Aucun jeton n'est manipulé ici.** La session vit dans un cookie `HttpOnly`
 * posé par le portail : le navigateur l'envoie seul, et le JavaScript ne peut
 * pas le lire — c'est précisément ce qu'on veut. En contrepartie, chaque
 * écriture doit venir de notre origine, et le portail le vérifie (P17.2).
 */

/** Le corps d'erreur du portail, dans l'esprit de RFC 7807. */
export interface Probleme {
  status: number;
  title: string;
  detail?: string;
  request_id?: string;
  /** Sur un 409 « Référence déjà prise » : l'affaire qui occupe la place, pour
   *  que l'écran propose de l'ouvrir plutôt que de laisser recommencer. */
  affaire_id?: string;
}

/** Une réponse refusée, avec de quoi l'afficher **et** de quoi la retrouver. */
export class ErreurApi extends Error {
  constructor(
    readonly statut: number,
    readonly probleme: Probleme,
  ) {
    super(probleme.detail || probleme.title || `Erreur ${statut}`);
    this.name = "ErreurApi";
  }

  /** L'appelant n'est pas — ou n'est plus — connecté. */
  get nonAuthentifie(): boolean {
    return this.statut === 401;
  }

  /** L'objet a changé, ou l'état de l'affaire ne permet pas ce geste. */
  get conflit(): boolean {
    return this.statut === 409;
  }
}

export interface Page<T> {
  donnees: T[];
  suite: string | null;
}

async function demander<T>(
  chemin: string,
  options: RequestInit & { version?: number } = {},
): Promise<T> {
  const { version, ...reste } = options;
  const entetes = new Headers(reste.headers);
  if (reste.body) entetes.set("Content-Type", "application/json");
  // La version lue, renvoyée telle quelle : c'est ce qui fait qu'une écriture
  // ne passe pas par-dessus celle d'un collègue.
  if (version !== undefined) entetes.set("If-Match", String(version));

  const reponse = await fetch(chemin, { ...reste, headers: entetes });

  if (reponse.status === 204) return undefined as T;
  if (!reponse.ok) {
    // Le portail rend toujours un corps structuré ; un intermédiaire, non.
    const probleme = await reponse
      .json()
      .catch(() => ({ status: reponse.status, title: reponse.statusText }));
    throw new ErreurApi(reponse.status, probleme as Probleme);
  }
  return (await reponse.json()) as T;
}

export const api = {
  lire: <T>(chemin: string) => demander<T>(chemin),

  ecrire: <T>(chemin: string, corps?: unknown, version?: number) =>
    demander<T>(chemin, {
      method: "POST",
      body: corps === undefined ? undefined : JSON.stringify(corps),
      version,
    }),

  modifier: <T>(chemin: string, corps: unknown, version?: number) =>
    demander<T>(chemin, {
      method: "PATCH",
      body: JSON.stringify(corps),
      version,
    }),

  // PUT : poser une ressource idempotente (un upsert par clé), là où POST
  // créerait un doublon et PATCH supposerait qu'elle existe déjà.
  remplacer: <T>(chemin: string, corps: unknown, version?: number) =>
    demander<T>(chemin, {
      method: "PUT",
      body: JSON.stringify(corps),
      version,
    }),

  retirer: <T>(chemin: string, version?: number) =>
    demander<T>(chemin, { method: "DELETE", version }),

  /**
   * Parcourt une liste page par page, jusqu'au bout.
   *
   * `limite` borne le nombre de pages : une boucle sans borne sur un curseur
   * qu'on aurait mal compris tournerait indéfiniment, et c'est le genre de
   * défaut qui ne se voit qu'en production sur un gros locataire.
   */
  async toutesLesPages<T>(chemin: string, limite = 40): Promise<T[]> {
    const tout: T[] = [];
    let curseur: string | null = null;
    for (let page = 0; page < limite; page += 1) {
      const url = curseur
        ? `${chemin}${chemin.includes("?") ? "&" : "?"}curseur=${encodeURIComponent(curseur)}`
        : chemin;
      const issue: Page<T> = await demander<Page<T>>(url);
      tout.push(...issue.donnees);
      if (!issue.suite) return tout;
      curseur = issue.suite;
    }
    return tout;
  },
};
